<template>
  <div class="d-flex flex-column">
    <div class="grid-header">
      <h1>{{ $t("clientes.titulo") }}</h1>
      <div class="toolbar">
        <button type="button" @click.prevent="crearClienteInmobiliario()" class="btn btn-link">
          <i class="fa-solid fa-plus"></i>
        </button>
      </div>
    </div>

    <DxDataGrid v-bind="getDefaultGridProperties()" :data-source="gridSource" ref="grid" class="dx-card wide-card grid-clientes">
      <DxColumnFixing :enabled="true" />
      <DxColumn data-field="NombreCompleto" :caption="$t('clientes.grid.nombre')" data-type="string"></DxColumn>
      <DxColumn data-field="Documento" :caption="$t('clientes_inmobiliarios.grid.documento')" data-type="string"
        :customize-text="customizeTextUppercase"></DxColumn>
      <DxColumn data-field="Email" :caption="$t('clientes.grid.email')" data-type="string"></DxColumn>
      <DxColumn data-field="TelefonoMovil" :caption="$t('clientes.grid.telefono')" data-type="string"></DxColumn>
      <DxColumn data-field="PuedeCrearBorrador" :caption="$t('clientes.grid.firmaAceptada')" cell-template="booleanTemplate"></DxColumn>
      <DxColumn data-field="AgenteReferencia" :caption="$t('clientes.grid.agente_referencia')" data-type="string"></DxColumn>

      <DxColumn type="buttons" :caption="$t('clientes.grid.acciones')" :fixed="true"
        fixed-position="right" :buttons="[
          {
            cssClass: 'btn btn-link text-decoration-none btn-sm',
            icon: 'fa-regular fa-edit text-success',
            hint: $t('clientes.grid.modificar'),
            onClick: (e: any) => verDetalle(e.row.data.Id)
          },
          {
            cssClass: 'btn btn-link text-decoration-none btn-sm',
            icon: 'fa-regular fa-file-export text-success',
            hint: $t('inmuebles.grid.enviar_autorizacion'),
            onClick: (e: any) => enviarAutorizacionTratamientoDatos(e.row.data.Id),
            disabled: (options: any) => !options.row?.data.PuedeEnviarAutorizacion
          },
          {
            icon: 'fa-regular fa-file-upload text-success',
            hint: $t('clientes.grid.subir_firma_a_mano'),
            onClick: (e: any) => subirFirmaAMano(e.row.data.Id, e.row.data.RutaAutorizacionFirmada),
            disabled: (options: any) => !options.row?.data.PuedeEnviarAutorizacion
          },
          {
            cssClass: 'btn btn-link text-decoration-none btn-sm',
            icon: 'fa-regular fa-file-magnifying-glass text-success',
            hint: $t('clientes.grid.ver_autorizacion'),
            onClick: (e: any) => verAutorizacion(e.row.data),
            disabled: (options: any) => !options.row?.data.RutaAutorizacionFirmada
          },
          {
            cssClass: 'btn btn-link text-decoration-none btn-sm',
            icon: 'fa-regular fa-envelope text-success',
            hint: $t('clientes.grid.enviar_recordatorio_firma'),
            onClick: (e: any) => enviarRecordatorioFirma(e.row.data.Id),
            disabled: (options: any) => !options.row?.data.PuedeEnviarRecordatorioFirmaAutorizacion
          },
          {
            cssClass: 'btn btn-link text-decoration-none btn-sm',
            icon: 'fa-regular fa-xmark text-success',
            hint: $t('clientes.grid.cancelar_firma'),
            onClick: (e: any) => cancelarFirma(e.row.data.Id),
            disabled: (options: any) => !options.row?.data.PuedeCancelarFirmaAutorizacion
          },
          {
            cssClass: 'btn btn-link text-decoration-none btn-sm',
            icon: 'fa-regular fa-plus text-success',
            hint: $t('clientes.grid.nueva_venta'),
            onClick: (e: any) => nuevaVenta(e.row.data.Id),
            disabled: (options: any) => !options.row?.data.PuedeCrearBorrador
          }
        ]"/>
        
      <template #booleanTemplate="{ data }">
        <BooleanTemplate :value="data.row?.data.PuedeCrearBorrador" />
      </template>
    </DxDataGrid>
  </div>
</template>

<script setup lang="ts">
import { useApi } from "@api/integration";
import {
  DxDataGrid,
  DxColumnFixing,
  DxColumn,
} from "devextreme-vue/data-grid";
import {
  customizeTextUppercase,
  getDefaultGridProperties,
} from "@helpers/grid-helper";
import type CustomStore from "devextreme/data/custom_store";
import { onMounted, ref } from "vue";
import { TipoPersonaEnum, type DXLoadOptions } from "@/.shared/.api";
import { useModalService } from "@/.shared/services/modal-service";
import BooleanTemplate from "@/.shared/components/BooleanTemplate.vue";
import { useIntl } from "@/.shared/services/i18n";
import ClienteInmobiliarioForm from "@/components/clientes-inmobiliarios/ClienteInmobiliarioForm.vue";
import { messageBoxResult } from "@/.shared/components/dialogs/message-box";
import { toastError, toastSuccess } from "@/.shared/services/notification-service";
import InputTextBox from '@/.shared/components/dialogs/InputTextBox.vue';
import { useRouter } from "@/.shared/services/routing-services";
import type { IViewerDocumentBoxProps } from "@components/dialogs/models/IViewerDocumentBoxProps";
import { Constants } from "@constants/ClienteInmobiliario";

interface ModalResult {
  result: string;
  value: string | null;
}

const api = useApi();
const gridSource = ref<CustomStore>();
const grid = ref<DxDataGrid>();
const modal = useModalService();
const intl = useIntl();
const router = useRouter();

onMounted(async () => {
  gridSource.value = api.apiStore({
    key: "Id",
    load: async (p) => {
      const q = p as DXLoadOptions;
      return await api.callStandardApi("getClientesEmpresaInmobiliariaForGrid", { showLoading: false }, q)
    }
  });
});

const crearClienteInmobiliario = () => {
  let title = `${intl.i18n.t("clientes.creacion.titulo")}`;
  modal.showModal(
    ClienteInmobiliarioForm,
    { isEdit: false },
    { title: title, width: 800, height: 600 }
  ).then((value) => {
    if (value == messageBoxResult.ok)
      grid.value?.instance?.refresh();
  });
}

const verDetalle = (id: string) => {
  let title = `${intl.i18n.t("clientes.edicion.titulo")}`;
  modal.showModal(
    ClienteInmobiliarioForm,
    { isEdit: true, idClienteInmobiliario: id },
    { title: title, width: 1100, height: 600 }
  ).then((value) => {
    if (value == messageBoxResult.ok)
      grid.value?.instance?.refresh();
  });
}

const enviarAutorizacionTratamientoDatos = async (idClienteInmobiliario: string) => {
  const emailAlternativo = await modal.showModal(
    InputTextBox,
    {
      label: intl.i18n.t('relacionClienteInmobiliario.ui.email_alternativo'),
      required: false,
      isEmail: true
    },
    {
      title: intl.i18n.t('relacionClienteInmobiliario.ui.titulo_email_alternativo'),
      width: 600,
      height: 280
    }
  ) as ModalResult;

  if (emailAlternativo?.result === messageBoxResult.ok) {
    await api.callStandardApi("iniciarFirmaAutorizacionClienteInmobiliario", { showLoading: true }, idClienteInmobiliario, { Email: emailAlternativo.value })
      .then(() => {
        toastSuccess(intl.i18n.t('relacionClienteInmobiliario.ui.autorizacion_enviada'));
        grid.value?.instance?.refresh();
      })
      .catch(() => toastError(intl.i18n.t('relacionClienteInmobiliario.ui.autorizacion_fallo')));
  }
}


const nuevaVenta = async (id: string) => {
  await router.push(intl.i18Route({ name: "nueva-venta-inmobiliaria", query: { idClienteInmobiliario: id } }));
}

const verAutorizacion = async (data: any) => {
  const props: IViewerDocumentBoxProps = {
    title: "Autorización",
    align: "horizontal",
    documentList: [
      {
        id: data.Id as string,
        uri: data.RutaAutorizacionFirmada as string,
        fileName: "Autorización tratamiento de datos"
      }
    ]
  };

  await modal.viewerDocumentBox(props, intl.i18n.t('clientes.grid.ver_autorizacion'));
}

const subirFirmaAMano = async (idClienteInmobiliario: string, ruta: string | undefined) => {

  const detailCliente = await api.callStandardApi("getClienteInmobiliarioById", { showLoading: true }, idClienteInmobiliario);
  var tipoPersona = detailCliente.TipoPersona;

  var documentUriTemplate = await api.callStandardApi("getPlantillaAutorizacionClienteInmobiliario", { showLoading: true }, idClienteInmobiliario);

  const autorizacionPropia = await modal.uploadFileBox({ title: intl.i18n.t('clientes.grid.autorizacion_propia'), required: true,
   documentUri: ruta, templateUri: documentUriTemplate},  intl.i18n.t('clientes.grid.subir_firma_a_mano'));
  
  if (!!autorizacionPropia) {
    try {
      await api.callStandardApi("subirAutorizacionClienteInmobiliario", { showLoading: false }, idClienteInmobiliario, autorizacionPropia.name, autorizacionPropia);

      toastSuccess(intl.i18n.t('clientes.grid.autorizacion_subida'));
      grid.value?.instance!.refresh();
    }
    catch (e: any) {
      toastError(intl.i18n.t('clientes.grid.autorizacion_subida_fallo'));
    }
  }
}

const enviarRecordatorioFirma = async (idClienteInmobiliario: string) => {

  const emailAlternativo = await modal.inputBox(intl.i18n.t('relacionClienteInmobiliario.ui.email_alternativo'), "dxTextBox", {isEmail: true}); 

  if(emailAlternativo === undefined) {
    return;
  }

  try {
    await api.callStandardApi("enviarRecordatorioFirmaAutorizacionClienteInmobiliario", { showLoading: true }, idClienteInmobiliario, emailAlternativo!);
    toastSuccess(intl.i18n.t('clientes.grid.recordatorio_enviado'));
    grid.value?.instance?.refresh();
  } catch (error) {
    toastError(intl.i18n.t('clientes.grid.recordatorio_fallo'));
  }
}

const cancelarFirma = async (idClienteInmobiliario: string) => {
  try {
    await api.callStandardApi("cancelarFirmaAutorizacionClienteInmobiliario", { showLoading: true }, idClienteInmobiliario);
    toastSuccess(intl.i18n.t('clientes.grid.firma_cancelada'));
    grid.value?.instance?.refresh();
  } catch (error) {
    toastError(intl.i18n.t('clientes.grid.firma_cancelada_fallo'));
  }
}

</script>
